import { ActiveWidgets } from 'modules/Editor/store/widgetControlSlice';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';

import { MULTI_WIDGET_TOOLBAR_CONFIG } from '../../../Widget.config';
import { getWidgetTypeFromId } from '../../../Widget.helpers';
import { MultiEditOption, MultiEditWidgetTypes } from '../../../Widget.types';

/**
 * Takes the active widgets and returns a list with widget ids (widgets that support multi editing)
 * Considers group members as well
 *
 * Special case: If only a whole group is selected (id equals groupId), we include all the group members
 *
 * @param activeWidgets - the active widgets
 */
const getAllMultiEditWidgetIds = (activeWidgets: ActiveWidgets) => {
  let allIds: WidgetId[] = [];

  // Special case: Single selected widget that is a whole group (id === groupId)
  if (
    activeWidgets.length === 1 &&
    activeWidgets[0].groupMembers.length > 0 &&
    activeWidgets[0].id === activeWidgets[0].groupId
  ) {
    // This is a whole group selection - use the member widget IDs instead
    const memberIds = activeWidgets[0].groupMembers;

    // Filter members to only include those that support multi-edit
    for (let i = 0; i < memberIds.length; i++) {
      const memberId = memberIds[i];
      const type = getWidgetTypeFromId(memberId);
      const isMultiEditWidget = MULTI_WIDGET_TOOLBAR_CONFIG[type as MultiEditWidgetTypes];

      if (isMultiEditWidget && !allIds.includes(memberId)) {
        allIds.push(memberId);
      }
    }

    return allIds;
  }

  // Normal case: Process individual active widgets
  for (let i = 0; i < activeWidgets.length; i++) {
    const activeWidget = activeWidgets[i];

    const type = getWidgetTypeFromId(activeWidget.id);
    const isMultiEditWidget = MULTI_WIDGET_TOOLBAR_CONFIG[type as MultiEditWidgetTypes];

    // if the widget is a multi edit widget, add it to the list of ids
    if (isMultiEditWidget && !allIds.includes(activeWidget.id)) {
      allIds.push(activeWidget.id);
    }
  }

  return allIds;
};

/**
 * Get the multi-edit toolbar options from the active widgets
 * @param activeWidgets
 *
 * returns MultiEditOption[]
 */
const getToolbarOptions = (activeWidgets: ActiveWidgets) => {
  const options: MultiEditOption[] = [];
  const widgetIds = getAllMultiEditWidgetIds(activeWidgets);
  const typeMap: { [key in WidgetType]?: boolean } = {};

  for (let i = 0; i < widgetIds.length; i++) {
    const widgetType = getWidgetTypeFromId(widgetIds[i]);

    // if we've already added the options for this widget type, continue
    if (typeMap[widgetType]) continue;

    typeMap[widgetType] = true;
    const widgetOptions = MULTI_WIDGET_TOOLBAR_CONFIG[widgetType as MultiEditWidgetTypes];

    if (!widgetOptions) continue;

    // non-text should always be first
    if (widgetType !== WidgetType.Text) {
      options.unshift(...widgetOptions);
      continue;
    }

    options.push(...widgetOptions);
  }

  // only return unique options
  return [...new Set(options)];
};

/**
 * Sorts widget types according to their position in the allowedTypes array
 * Allowed types come first, followed by other types in alphabetical order
 * @param widgetTypes - The array of widget types to sort (sorted in place)
 * @param allowedTypes - The ordered array of allowed widget types
 */
const sortWidgetTypes = (widgetTypes: string[], allowedTypes: WidgetType[]) => {
  widgetTypes.sort((a, b) => {
    const indexA = allowedTypes.indexOf(a as WidgetType);
    const indexB = allowedTypes.indexOf(b as WidgetType);

    if (indexA >= 0 && indexB >= 0) {
      return indexA - indexB;
    }

    if (indexA >= 0) return -1;
    if (indexB >= 0) return 1;

    return a.localeCompare(b);
  });
};

export { getAllMultiEditWidgetIds, getToolbarOptions, sortWidgetTypes };
